import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { CobuText } from '@cobu/ui';
import { Strings } from '../../constants/Strings';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { showErrorMessage, showInfoMessage } from '../../utils/messageToast';
import { Gathering } from '@cobu/types';
import { logger } from '../../utils/logger';
import { postToNative, requestNativePermission } from '../../lib/session/nativeMessaging';
import { RootState } from '../../store';
import { updateNativePermission } from '../../store/authSlice';
interface CalendarButtonProps {
  gathering: Gathering;
}

export default function CalendarButton({ gathering }: CalendarButtonProps) {
  const [isAdded, setIsAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { nativePermissions, nativePermissionsProvided } = useSelector(
    (state: RootState) => state.auth
  );
  const {
    title,
    description,
    locationString,
    scheduledStartTime,
    scheduledEndTime,
  } = gathering;

  const formatDateForIcs = useCallback((date: Date) => {
    // YYYYMMDDTHHmmssZ in UTC
    return dayjs(date).utc().format('YYYYMMDD[T]HHmmss[Z]');
  }, []);

  const escapeText = useCallback((text: string) => {
    return (text || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  }, []);

  const buildICS = useCallback(() => {
    const start = new Date(scheduledStartTime);
    const end = scheduledEndTime
      ? new Date(scheduledEndTime)
      : dayjs(scheduledStartTime).add(2, 'hours').toDate();

    const uid = `${start.getTime()}-${Math.random().toString(36).slice(2)}@cobu.app`;
    const dtStamp = formatDateForIcs(new Date());
    const dtStart = formatDateForIcs(start);
    const dtEnd = formatDateForIcs(end);

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cobu//PWA//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${escapeText(title || Strings.get().ui.calendar.eventTitleFallback)}`,
      `DESCRIPTION:${escapeText(description || '')}`,
      `LOCATION:${escapeText(locationString || '')}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    return ics;
  }, [scheduledStartTime, scheduledEndTime, title, description, locationString, formatDateForIcs, escapeText]);

  const triggerDownload = useCallback((filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, []);

  const buildGoogleCalendarUrl = useCallback(() => {
    const start = dayjs(scheduledStartTime).utc().format('YYYYMMDDTHHmmss[Z]');
    const end = (scheduledEndTime
      ? dayjs(scheduledEndTime)
      : dayjs(scheduledStartTime).add(2, 'hours')
    ).utc().format('YYYYMMDDTHHmmss[Z]');

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title || Strings.get().ui.calendar.eventTitleFallback,
      details: description || '',
      location: locationString || '',
      dates: `${start}/${end}`,
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }, [scheduledStartTime, scheduledEndTime, title, description, locationString]);

  const openGoogleCalendar = useCallback(() => {
    const webUrl = buildGoogleCalendarUrl();
  
    // Try to open in a new tab/window (browsers)
    const win = window.open(webUrl, '_blank', 'noopener');
  
    // Fallback for environments like WebView where window.open may be blocked/ignored
    if (!win) {
      window.location.href = webUrl;
    }
  }, [buildGoogleCalendarUrl]);

  /**
   * Sends calendar event data to iOS native app.
   * iOS native app should listen for messages with type: 'COBU_ADD_TO_CALENDAR'
   */
  const sendCalendarEventToNative = useCallback(() => {
    const startDate = new Date(scheduledStartTime);
    const endDate = scheduledEndTime
      ? new Date(scheduledEndTime)
      : dayjs(scheduledStartTime).add(2, 'hours').toDate();

    const payload = {
      source: 'Cobu',
      type: 'COBU_ADD_TO_CALENDAR',
      event: {
        eventId: gathering.uuid,
        title: title || Strings.get().ui.calendar.eventTitleFallback,
        description: description || '',
        location: locationString || '',
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        // Also include Unix timestamps for easier parsing
        startTimestamp: startDate.getTime(),
        endTimestamp: endDate.getTime(),
      },
    };

    return postToNative(payload);
  }, [gathering.uuid, title, description, locationString, scheduledStartTime, scheduledEndTime]);

  /**
   * Check if running inside iOS native app WebView
   */
  const isIOSNativeWebView = useCallback(() => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const hasNativeBridge = !!(
      window.ReactNativeWebView?.postMessage ||
      (window as any).CobuNative?.postMessage
    );
    return isIOS && hasNativeBridge;
  }, []);

  const checkAndRequestPermission = useCallback(
    async (): Promise<boolean> => {
      // Only check if native permissions are provided
      if (!nativePermissionsProvided) {
        return true;
      }
      
      // Check if calendar permission param is available
      // If calendar is undefined, it means the param is not available, so keep current flow
      if (nativePermissions.calendar === undefined) {
        return true;
      }
      
      // If calendar permission is already granted, proceed
      if (nativePermissions.calendar) {
        return true;
      }

      // Bypass "Go to Settings" prompt — request permission directly on the original click
      try {
        const granted = await requestNativePermission('calendar', 'add_to_calendar');
        dispatch(updateNativePermission({ permission: 'calendar', granted }));
        return granted;
      } catch (_) {
        return false;
      }

    },
    [nativePermissions, nativePermissionsProvided, dispatch]
  );

  const handleAddToCalendar = useCallback(async () => {
    if (isAdded || isLoading) return;

    // Check calendar permission first (only if param is available)
    const hasPermission = await checkAndRequestPermission();
    if (!hasPermission) {
      return;
    }

    try {
      setIsLoading(true);

      // Track analytics
      /* ANALYTICS_DISABLED: ADD_EVENT_TO_CALENDAR ('add_event_to_calendar')
        analyticsService.trackEvent(AnalyticsEvent.ADD_EVENT_TO_CALENDAR, {
                eventId: gathering.uuid,
                eventTitle: title,
              });
        */

      // Android: use Google Calendar URL (Android native app handles this via their own listener)
      const isAndroid = /Android/i.test(navigator.userAgent);
      if (isAndroid) {
        try {
          openGoogleCalendar();
          /* ANALYTICS_DISABLED: ALLOW_ACCESS_TO_CALENDAR ('allow_access_to_calendar')
            analyticsService.trackEvent(AnalyticsEvent.ALLOW_ACCESS_TO_CALENDAR);
            */
          // Do not set isAdded; user must save the event in Google Calendar.
          return;
        } catch {
          // Fallback to ICS below
        }
      }

      // iOS WebView: send event data to native iOS app
      // Permission already checked at the beginning of this function
      if (isIOSNativeWebView()) {
        const sent = sendCalendarEventToNative();
        if (sent) {
          /* ANALYTICS_DISABLED: ALLOW_ACCESS_TO_CALENDAR ('allow_access_to_calendar')
            analyticsService.trackEvent(AnalyticsEvent.ALLOW_ACCESS_TO_CALENDAR);
            */
          logger.info('[CalendarButton] Sent calendar event to iOS native app');
          // iOS native app handles the rest - don't set isAdded as user confirms in native UI
          return;
        }
        // If sending failed, fall through to ICS fallback
      }

      // Default (iOS Safari / Desktop): download ICS file
      const ics = buildICS();
      const filename = `${(title || 'event').replace(/\s+/g, '_')}.ics`;
      triggerDownload(filename, ics);
      setIsAdded(true);

      const isMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (!isMobile) {
        showInfoMessage(`"${title}" calendar file downloaded. Open it to add to your calendar.`);
      }
    } catch (error) {
      logger.error('Error generating ICS file:', error);
      showErrorMessage('Failed to create calendar file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isAdded, isLoading, isIOSNativeWebView, sendCalendarEventToNative, buildICS, triggerDownload, title, openGoogleCalendar, gathering.uuid, checkAndRequestPermission, nativePermissionsProvided, nativePermissions]);

  // Don't render if event is in the past
  if (dayjs().isAfter(scheduledStartTime)) {
    return null;
  }

  const s = Strings.get().ui.calendar;
  const buttonText = isAdded
    ? s.addedToCalendar
    : isLoading
    ? s.addingToCalendar
    : s.addToCalendar;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleAddToCalendar}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleAddToCalendar();
        }
      }}
      style={{ alignSelf: 'flex-start', cursor: isAdded || isLoading ? 'default' : 'pointer', opacity: isAdded || isLoading ? 0.7 : 1 }}
    >
      <CobuText
        variant="body"
        fos="$2"
        fow="bold"
        col={isAdded ? "$colorMuted" : "$textLinkColor"}
      >
        {buttonText}
      </CobuText>
    </div>
  );
}
